'use client'

import clsx from 'clsx'
import { LayoutGroup } from 'motion/react'
import React, { forwardRef, useId } from 'react'
import type { ComponentPropsWithoutRef, ForwardedRef, ReactNode } from 'react'
import { TouchTarget } from './button'
import { Link } from '../shared/link'
import { themeClasses } from '../shared/themeClasses'

type SidebarProps = ComponentPropsWithoutRef<'nav'>
type SidebarBodyProps = ComponentPropsWithoutRef<'div'>
type SidebarFooterProps = ComponentPropsWithoutRef<'div'>
type SidebarSectionProps = ComponentPropsWithoutRef<'div'>
type SidebarSpacerProps = ComponentPropsWithoutRef<'div'>
type SidebarLabelProps = ComponentPropsWithoutRef<'span'>

export function Sidebar({ className, ...props }: SidebarProps) {
  return <nav {...props} className={clsx(className, 'flex h-full min-h-0 w-full min-w-0 flex-col')} />
}

export function SidebarBody({ className, ...props }: SidebarBodyProps) {
  return <div {...props} className={clsx(className, 'flex flex-1 flex-col overflow-y-auto p-4 [&>[data-slot=section]+[data-slot=section]]:mt-8')} />
}

export function SidebarFooter({ className, ...props }: SidebarFooterProps) {
  return <div {...props} className={clsx(className, 'flex flex-col border-t p-4 [&>[data-slot=section]+[data-slot=section]]:mt-2.5', themeClasses.dividerBorderSoft)} />
}

export function SidebarSection({ className, ...props }: SidebarSectionProps) {
  const id = useId()

  return (
    <LayoutGroup id={id}>
      <div {...props} data-slot="section" className={clsx(className, 'flex flex-col gap-0.5')} />
    </LayoutGroup>
  )
}

export function SidebarSpacer({ className, ...props }: SidebarSpacerProps) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'mt-8 flex-1')} />
}

type SidebarItemBaseProps = {
  current?: boolean
  className?: string
  children?: ReactNode
  href?: string
}

type SidebarItemActionProps = Omit<ComponentPropsWithoutRef<'button'>, 'className' | 'children' | 'href'> &
  Omit<ComponentPropsWithoutRef<'a'>, 'className' | 'children' | 'href'>

type SidebarItemProps = SidebarItemBaseProps & SidebarItemActionProps

export const SidebarItem = forwardRef<HTMLElement, SidebarItemProps>(function SidebarItem(
  { current, className, children, href, ...props },
  ref
) {
  const classes = clsx(
    'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 sm:py-2 sm:text-sm/5',
    themeClasses.textPrimary,
    '*:data-[slot=icon]:size-6 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:size-5',
    themeClasses.itemIconFillMuted,
    '*:last:data-[slot=icon]:ml-auto *:last:data-[slot=icon]:size-5 sm:*:last:data-[slot=icon]:size-4',
    '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7 sm:*:data-[slot=avatar]:size-6',
    'hover:bg-zinc-950/5 hover:*:data-[slot=icon]:fill-zinc-950 active:bg-zinc-950/10 dark:hover:bg-white/5 dark:hover:*:data-[slot=icon]:fill-white dark:active:bg-white/10',
    themeClasses.itemCurrentIconFill,
    current && 'bg-zinc-950/5 dark:bg-white/5'
  )
  const itemClasses = clsx(classes, className)

  return (
    <span className="relative">
      {typeof href === 'string' ? (
        <Link
          {...(props as Omit<ComponentPropsWithoutRef<typeof Link>, 'className' | 'children' | 'href' | 'type'>)}
          href={href}
          className={itemClasses}
          data-current={current ? 'true' : undefined}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <button
          {...(props as Omit<ComponentPropsWithoutRef<'button'>, 'className' | 'children'>)}
          type={(props as ComponentPropsWithoutRef<'button'>).type ?? 'button'}
          className={clsx('cursor-pointer', itemClasses)}
          data-current={current ? 'true' : undefined}
          data-disabled={(props as ComponentPropsWithoutRef<'button'>).disabled ? 'true' : undefined}
          ref={ref as ForwardedRef<HTMLButtonElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </button>
      )}
    </span>
  )
})

export function SidebarLabel({ className, ...props }: SidebarLabelProps) {
  return <span {...props} className={clsx(className, 'truncate')} />
}
